interface DataLayerItem {
  item_id: string;
  item_name: string;
  index: number;
}

interface BaseDataLayer {
  event?: string;
  [key: string]: any;
}

interface SearchResultsDataLayer extends BaseDataLayer {
  view_search_results: {
    search_term: string | string[] | undefined;
    result_count: number;
    search_url: string;
    interaction: string;
  };
    ecommerce: {
      item_list_id: string;
      item_list_name: string;
      items: DataLayerItem[];
    };
}

interface SearchDataLayer extends BaseDataLayer {
  event: "search";
  search: {
    search_term: string | string[] | undefined;
    interaction: "filter_interaction";
  };
}

interface FilterInteractionDataLayer extends BaseDataLayer {
  event: "filter_apply";
  filter_apply: { 
    result_count: number;
    filter_type: string;
    filter_value: string | string[] | undefined;
    isAgent: boolean;
    action?: string;
  };
}

interface SearchParams {
  [key: string]: string | string[] | undefined;
}

// State management for URL comparison
let previousSearchUrl: string | null = null;

// Define search term parameters (order doesn't matter - we track the latest added parameter)
const SEARCH_TERM_FIELDS = [
  "TourId", "City", "Region", "Country",
  "Continent", "Badges", "TourStyles", "Offers", "months", "CustomerReview",
  "ActivityLevel", "TourLengthDescription", "PriceFrom", "PriceTo", "sortBy"
];

// Helper function to extract search term parameters from URL
const extractSearchTermParams = (url: string): Record<string, string | string[]> => {
  const urlObj = new URL(url);
  const params: Record<string, string | string[]> = {};

  SEARCH_TERM_FIELDS.forEach(field => {
    const value = urlObj.searchParams.get(field);
    if (value) {
      // Handle comma-separated values as arrays
      params[field] = value.includes(",") ? value.split(",") : value;
    }
  });

  return params;
};

// Helper function to find the newly added search term parameter
const findNewlyAddedSearchTerm = (
  previousParams: Record<string, string | string[]>,
  currentParams: Record<string, string | string[]>,
  searchParams: SearchParams | undefined
): string | string[] | undefined => {
  if (!searchParams) return undefined;

  // Find parameters that are new or changed
  const newOrChangedFields = SEARCH_TERM_FIELDS.filter(field => {
    const prevValue = previousParams[field];
    const currentValue = currentParams[field];

    // Return true if current has a value that previous didn't have, or values changed
    return currentValue && (!prevValue || JSON.stringify(prevValue) !== JSON.stringify(currentValue));
  });

  // If we found new/changed parameters, return the value of the first one found
  // (since we're not using priority order, just take the first newly added one)
  if (newOrChangedFields.length > 0) {
    const newField = newOrChangedFields[0];
    return searchParams[newField as keyof SearchParams];
  }

  return undefined;
};

// Helper function to check if any search terms were added or changed
const hasNewOrChangedSearchTerms = (
  previousParams: Record<string, string | string[]>,
  currentParams: Record<string, string | string[]>
): boolean => SEARCH_TERM_FIELDS.some(field => {
  const prevValue = previousParams[field];
  const currentValue = currentParams[field];

  // If current has a value that previous didn't have, or values changed
  return Boolean(currentValue && (!prevValue || JSON.stringify(prevValue) !== JSON.stringify(currentValue)));
});

// Helper function to get any available search term (fallback for first load)
const getAnyAvailableSearchTerm = (params: SearchParams | undefined): string | string[] | undefined => {
  if (!params) return undefined;

  // Find the first available search term parameter (no priority)
  const availableField = SEARCH_TERM_FIELDS.find(field => {
    const value = params[field as keyof SearchParams];
    if (value !== undefined && value !== null && value !== "") {
      return Array.isArray(value) ? value.length > 0 : true;
    }
    return false;
  });

  if (availableField) {
    return params[availableField as keyof SearchParams];
  }

  return undefined;
};

export const createSearchDataLayer = (
  searchTerm: string | string[] | undefined
): SearchDataLayer => ({
  event: "search",
  search: {
    search_term: searchTerm,
    interaction: "filter_interaction"
  }
});

export const createSearchResultsDataLayer = (
  searchParams: SearchParams | undefined,
  resultCount: number,
  searchUrl: string,
  interaction: string,
  searchResults: Array<{ tourId: string; title: string }>
): SearchResultsDataLayer => {
  let searchTerm: string | string[] | undefined;

  try {
    // Extract search term parameters from current and previous URLs
    const currentUrlParams = extractSearchTermParams(searchUrl);

    if (previousSearchUrl) {
      const previousUrlParams = extractSearchTermParams(previousSearchUrl);
      // Only set search term if there are new or changed search term parameters
      if (hasNewOrChangedSearchTerms(previousUrlParams, currentUrlParams)) {
        // Find the specific newly added search term
        searchTerm = findNewlyAddedSearchTerm(previousUrlParams, currentUrlParams, searchParams);
      }
      // If no new/changed search terms, searchTerm remains undefined
    }

    // First time or no previous URL - use any available search term if parameters exist
    if (!previousSearchUrl && Object.keys(currentUrlParams).length > 0) {
      searchTerm = getAnyAvailableSearchTerm(searchParams);
    }

    // Update the stored previous URL
    previousSearchUrl = searchUrl;
  } catch (error) {
    // Fallback to original logic if URL parsing fails
    searchTerm = getAnyAvailableSearchTerm(searchParams);
  }

  // Extract the latest value from searchTerm (handle both arrays and comma-delimited strings)
  let finalSearchTerm: string | undefined;
  if (searchTerm) {
    if (Array.isArray(searchTerm)) {
      // If it's an array, take the last item (latest addition)
      finalSearchTerm = searchTerm.length > 0 ? searchTerm[searchTerm.length - 1] : undefined;
    } else if (typeof searchTerm === "string" && searchTerm.includes(",")) {
      // If it's a comma-delimited string, take the value after the last comma
      const parts = searchTerm.split(",");
      finalSearchTerm = parts[parts.length - 1].trim();
    } else {
      // If it's just a single string value, use it as-is
      finalSearchTerm = searchTerm;
    }
  }

  const items: DataLayerItem[] = searchResults.map((tour, index) => ({
    item_id: tour.tourId,
    item_name: tour.title,
    index
  }));

  return {
    view_search_results: {
      search_term: finalSearchTerm,
      result_count: resultCount,
      search_url: searchUrl,
      interaction
    },
    ecommerce: {
      item_list_id: "onsite_search",
      item_list_name: "Search Results",
      items
    }
  };
};

// Utility function to reset the URL comparison state (useful for testing or manual resets)
export const resetSearchUrlState = (): void => {
  previousSearchUrl = null;
};

// Utility function to get the current stored search URL (useful for debugging)
export const getCurrentStoredSearchUrl = (): string | null => previousSearchUrl;

// Future factory functions for other data layer events
export const createFilterInteractionDataLayer = (
  resultCount: number,
  filterType: string,
  filterValue: string | string[] | undefined,
  isAgent: boolean,
  action?: string
): FilterInteractionDataLayer => ({
  event: "filter_apply",
  filter_apply: {
    result_count: resultCount,
    filter_type: filterType,
    filter_value: filterValue,
    isAgent,
    action
  }
});
